import { Injector } from "@angular/core";
import { environment } from "../../../../environments/environment";
import { ObjectUtil } from "../../../components/common/object-util/object-util";
import { GuidService } from "../../project-valuation/services/guid.service";
import { ReportDesignerPageOptions } from "../models/report-designer-page-options";
import { ReportDesignerWidgetConfig } from "../models/report-designer-widget-config";
import { PrintServiceResolver } from "../services/print/print-service-resolver";
import { PrintService } from "../services/print/print.service";
import { VirtualPagesService } from "./virtual-pages-service";
import { WidgetBaseComponent } from "./widgetBase";

export type ProvideSaveObjectFn = () => Promise<string>;

/**
 * Base class for virtual (multipage) widgets
 */
export abstract class WidgetVirtualBase extends WidgetBaseComponent {

  /**
   * WIDGET_MASTER_SLAVE_MSG: used for communication between master and slave widgets
   */
  protected WIDGET_MASTER_SLAVE_MSG: string;

  /**
   * Is widget instance slave widget?
   */
  protected get IsSlaveWidget(): boolean {
    return !this.isMasterWidget && this.page.isVirtualPage;
  }

  /**
   * Array of page data
   */
  protected widgetPages: Array<any> = [];

  /**
   * Guid service
   */
  private guidService: GuidService;

  /**
   * Print service
   */
  private printService: PrintService;


  /**
   * Virtual pages service
   */
  protected virtualPagesService: VirtualPagesService;

  /**
   * Constructor
   */
  constructor(injector: Injector, name: string) {
    super(injector, name, false);
    this.guidService = injector.get(GuidService);
    this.printService = injector.get(PrintServiceResolver).get();
    this.virtualPagesService = injector.get(VirtualPagesService);
    this.WIDGET_MASTER_SLAVE_MSG = `WIDGET_MASTER_SLAVE_MSG_${name}`;
  }

  /**
   * Register Master widget events
   */
  abstract registerEventsMaster(): void;

  /**
   * Register Slave widgets events
   */
  abstract registerEventsSlave(): void;

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   * This function is called for Master widget
   */
  abstract onInitializedMaster(): void;

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   * This function is called for Slave widgets
   */
  abstract onInitializedSlave(): void;

  /**
   * Setup widget ViewModel
   */
  abstract setupWidgetViewModel(widgetPages: []): void;

  /**
   * Called by base class to let widget register to various app events (like saving, loading, resizing etc)
   */
  registerEvents(): void {
    if (this.isMasterWidget) {
      this.registerEventsMaster();
    } else {
      this.registerEventsSlave();
    }

    this.registerCommonEvents();
  }

  /**
   * Called by base class to inform widget that it is initialized
   * Mostly used to define where widget data should be loaded from
   */
  onInitialized(): void {
    if (this.isMasterWidget) {
      if (this.page) {
        this.page.page.isVirtualPageMaster = true;
      }
      this.onInitializedMaster();
      if (this.widgetPages.length == 1) {
        //no slave pages
      }
    } else {
      this.onInitializedSlave();
    }
  }

  /**
   * Process Widget's Slave Pages
   */
  protected processWidgetSlavePages(masterWidget: WidgetVirtualBase): void {

    if (this.widgetPages.length == 1) {
      return; //no slave pages
    }

    const masterPageIndex = masterWidget.page.pageIndex;

    const result = this.widgetService.getHPageConfig();
    const pageJson = result;
    const pageOptions = pageJson.options as ReportDesignerPageOptions;
    pageOptions.waitingForPrint = this.printService.pageWaitsForPrint;

    const clonedConfig = ObjectUtil.deepCopy(this.widgetConfig);
    pageJson.widgets.push(clonedConfig);
    const slaveWidget: ReportDesignerWidgetConfig = pageJson.widgets[0];
    slaveWidget.row = 0;
    slaveWidget.col = 0;
    slaveWidget.sizeY = 17;
    slaveWidget.minSizeY = 17;
    slaveWidget.minItemRows = 17;
    slaveWidget.rows = 17;

    slaveWidget.widgetSettings.availableLayouts = undefined;

    for (let idx = 1; idx < this.widgetPages.length; idx++) {
      if (!this.isSlavePageInDocument(idx)) {
        const nextPageNo = masterPageIndex + idx;

        slaveWidget.widgetSettings.slavePageNo = idx;//NOT REAL PAGE INDEX, BUT SLAVE PAGENO (1,2,..-N; 0 is MASTER) !!
        slaveWidget.widgetSettings.id = this.guidService.generate();

        //get custom settings from master widget
        slaveWidget.widgetSettings.customSettings = masterWidget.widgetConfig.widgetSettings.customSettings;

        const page = ObjectUtil.deepCopy(pageJson);
        page.isVirtualPage = true;

        this.page.document.addPage(page, nextPageNo, false);
      }
    }
  }

  /**
   * Checks if slave page is in document
   * @param slavePageNo {number | undefined} Slave Page number
   */
  private isSlavePageInDocument(slavePageNo: number): boolean {
    const pages = this.page.document.data.pages;
    for (let idx = 0, len = pages.length; idx < len; idx++) {
      const page = pages[idx];
      if (page.widgets.length === 1 && page.widgets[0].widgetId === this.widgetConfig.widgetId && page.widgets[0].widgetSettings.slavePageNo === slavePageNo) {
        return true;
      }
    }

    return false;
  }

  /**
   * Broadcast message from master widget
   * @param msg {any} Message to broacast
   */
  protected broadcastFromMaster(msg): void {
    if (this.isMasterWidget) {
      !environment.production && console.log(this.name + " broadcast MASTER: " + msg.cmd, msg);
      this.eventBus.broadcastEvent(this.WIDGET_MASTER_SLAVE_MSG, msg);
    }
  }

  /**
   * Broadcast message from slave widget
   * @param msg {any} Message to broacast
   */
  protected broadcastFromSlave(msg): void {
    if (!this.isMasterWidget) {
      !environment.production && console.log(this.name + " broadcast SLAVE: " + msg.cmd, msg);
      this.eventBus.broadcastEvent(this.WIDGET_MASTER_SLAVE_MSG, msg);
    }
  }

  /**
   * Register events common to master and slave widgets
   */
  private registerCommonEvents(): void {
    this.onEvents.push(this.eventBus.subscribeEvent(this.WIDGET_MASTER_SLAVE_MSG, (msg) => {
      if (this.isMasterWidget) {
        this.handleMasterWidgetMessages(msg);
      } else {
        this.handleSlaveWidgetMessages(msg);
      }
    }));
  }

  /**
   * Handles master widget messages
   * @param msg {any} Message to handle
   */
  private handleMasterWidgetMessages(msg: any): void {
    !environment.production && console.log(this.name + ":: MASTER on: " + msg.cmd + ": " + (msg.value || ""));
    switch (msg.cmd) {
      case "setDirtyFlag":
        this.setDirtyFlag(msg.value);
        return;
      case "getSlaveData":
        console.warn('Deprecated.. don\'t use!!!');
        //if page exists send it to slave page
        if (this.widgetPages[msg.value]) {
          const slavePageData = this.widgetPages[msg.value];
          slavePageData.slaveDataUpdated = true;
          this.broadcastFromMaster({ cmd: "setSlaveData", value: slavePageData });
        }
        return;
      case "deleteSlavePages":
        for (let idx = this.widgetPages.length - 1; idx > 0; idx--) {
          this.broadcastFromMaster({ cmd: "deleteSlavePage", value: this.widgetPages[idx].pageNo });
        }
        return;
      default:
        return;
    }
  }

  /**
   * Handles slave widgets messages
   * @param msg {any} Message to handle
   */
  private handleSlaveWidgetMessages(msg: any): void {
    if (this.widgetData.pageNo == msg.value.pageNo) {
      !environment.production && console.log("SLAVE on: " + msg.cmd, msg.value || "");
      switch (msg.cmd) {
        case "setSlaveData": {
          this.widgetData = msg.value;
          this.page.widgetDataLoaded(this.widgetConfig.widgetSettings.id);
          this.slaveWidgetMessageHandled(msg.cmd);
          return;
        }
        case "currentLayoutChanged":
          return;
        case "deleteSlavePage": {
          this.page.widgets.length = 0;
          const document = this.page.document;
          document.data.pages.splice(this.page.pageIndex, 1);
          document.syncToPageList();
          this.slaveWidgetMessageHandled(msg.cmd);
          return;
        }
        default:
          return;
      }
    }
  }

  /**
   * slaveWidgetMessageHandled default implementation (default do nothing)
   * @param cmd {any} Message handled
   */
  protected slaveWidgetMessageHandled(cmd: string): void {
    //default do nothing
  }
}
